<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { Button, Toggle } from '@Shelly/shelly-design-system/index';
	import { format } from 'svelte-i18n';

	export let title: string = 'Edit zone';
	export let drawModeName: string = '';
	export let drawModeColor: number[] = [];
	export let hasChanges: boolean = false;
	export let palette: readonly (readonly number[])[] = [];
	export let mode: 'draw' | 'erase' = 'draw';
	export let saving: boolean = false;
	export let enabled: boolean = true;

	const dispatch = createEventDispatcher();

	function handleNameInput(e: Event) {
		dispatch('nameInput', (e.target as HTMLInputElement).value);
	}
</script>

<div class="draw-sidebar">
	<p class="draw-sidebar__title">
		<span
			class="draw-sidebar__dot"
			style="background:{drawModeColor.length ? `rgb(${drawModeColor.join(',')})` : '#fff'}"
		></span>
		{title}
	</p>

	<!-- Name input -->
	<div class="draw-sidebar__field">
		<label class="draw-sidebar__label" for="draw-zone-name"
			>{$format('presence_draw_name_label', { default: 'Name' })}</label
		>
		<input
			id="draw-zone-name"
			class="draw-sidebar__input"
			type="text"
			value={drawModeName}
			disabled={saving}
			on:input={handleNameInput}
			aria-label="Zone name"
		/>
	</div>

	<!-- Enable / Disable toggle -->
	<div class="draw-sidebar__toggle-row">
		<label class="draw-sidebar__label" for="zone-enabled-toggle">
			{$format('presence_zone_enabled_label', { default: 'Zone enabled' })}
		</label>
		<Toggle
			id="zone-enabled-toggle"
			checked={enabled}
			disabled={saving}
			on:click={() => dispatch('enabledChange', !enabled)}
			aria-label={$format('presence_zone_enabled_label', { default: 'Zone enabled' })}
		/>
	</div>

	<!-- Draw / Erase pill (below the name input) -->
	<div class="draw-pill" role="group" aria-label="Drawing mode">
		<button
			class="draw-pill__btn {mode === 'draw' ? 'draw-pill__btn--active' : ''}"
			disabled={saving}
			on:click={() => dispatch('modeChange', 'draw')}
			aria-pressed={mode === 'draw'}
		>
			<i class="fat fa-pencil" aria-hidden="true"></i>
			{$format('button_draw', { default: 'Draw' })}
		</button>
		<button
			class="draw-pill__btn {mode === 'erase' ? 'draw-pill__btn--active' : ''}"
			disabled={saving}
			on:click={() => dispatch('modeChange', 'erase')}
			aria-pressed={mode === 'erase'}
		>
			<i class="fat fa-eraser" aria-hidden="true"></i>
			{$format('button_erase', { default: 'Erase' })}
		</button>
	</div>

	<!-- Color swatches -->
	<div class="draw-sidebar__field">
		<span class="draw-sidebar__label"
			>{$format('presence_draw_color_label', { default: 'Color' })}</span
		>
		<div class="draw-sidebar__swatches">
			{#each palette as c}
				{@const ca = [...c]}
				<button
					type="button"
					class="swatch {JSON.stringify(drawModeColor) === JSON.stringify(ca)
						? 'swatch--active'
						: ''}"
					style="background:rgb({ca.join(',')})"
					disabled={saving}
					on:click={() => dispatch('colorSelect', ca)}
					aria-label="rgb({ca.join(',')})"
					title="rgb({ca.join(',')})"
				></button>
			{/each}
		</div>
	</div>

	<div class="draw-sidebar__actions">
		{#if hasChanges}
			<Button variant="secondary" size="m" on:click={() => dispatch('discard')} disabled={saving}>
				{$format('button_discard', { default: 'Discard' })}
			</Button>
			<Button
				aria-label="save"
				variant="primary"
				size="m"
				on:click={() => dispatch('save')}
				disabled={saving}
			>
				{#if saving}
					<i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
					{$format('button_saving', { default: 'Saving…' })}
				{:else}
					{$format('button_save', { default: 'Save' })}
				{/if}
			</Button>
		{:else}
			<Button variant="secondary" size="m" disabled={saving} on:click={() => dispatch('return')}
				>{$format('button_return', { default: 'Return' })}</Button
			>
		{/if}
	</div>
</div>

<style>
	.draw-sidebar {
		padding: 12px 14px 8px;
		display: flex;
		flex-direction: column;
		gap: 14px;
	}

	.draw-sidebar__title {
		font-size: 16px;
		font-weight: 600;
		color: #eaeaea;
		display: flex;
		align-items: center;
		gap: 10px;
		margin: 0;
	}

	.draw-sidebar__dot {
		width: 16px;
		height: 16px;
		border-radius: 50%;
		flex-shrink: 0;
		display: inline-block;
	}

	.draw-sidebar__field {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.draw-sidebar__label {
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: #a1a2a2;
	}

	.draw-sidebar__input {
		height: 40px;
		padding: 0 12px;
		background: #1e2023;
		border: 1px solid #3a3d41;
		border-radius: 8px;
		color: #eaeaea;
		font-size: 14px;
		outline: none;
		transition: border-color 0.12s;
		width: 100%;
		box-sizing: border-box;
	}

	.draw-sidebar__input:focus {
		border-color: #2491ff;
	}

	/* ── Draw / Erase pill ───────────────────────────────────────────────────── */
	.draw-pill {
		display: flex;
		background-color: #191a1e;
		border: 1px solid #151617;
		border-radius: 200px;
		padding: 4px;
	}

	.draw-pill__btn {
		flex: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		height: 40px;
		border: none;
		border-radius: 200px;
		background: transparent;
		color: #eaeaea;
		font-size: 15px;
		font-weight: 500;
		cursor: pointer;
		transition: background-color 0.15s;
		white-space: nowrap;
	}

	.draw-pill__btn--active {
		background-color: #ffffff;
		color: #151617;
		font-weight: 700;
	}

	.draw-pill__btn:not(.draw-pill__btn--active):hover {
		background-color: rgba(255, 255, 255, 0.08);
	}

	.draw-sidebar__swatches {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	.swatch {
		width: 26px;
		height: 26px;
		border-radius: 50%;
		border: 2px solid transparent;
		cursor: pointer;
		position: relative;
		transition: transform 0.1s;
		flex-shrink: 0;
	}

	.swatch:hover {
		transform: scale(1.12);
	}

	.swatch--active {
		box-shadow: inset 0 0 0 2px #fff;
	}

	.swatch--active::after {
		content: '✓';
		font-size: 12px;
		color: #fff;
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}

	.draw-sidebar__toggle-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.draw-sidebar__actions {
		display: flex;
		gap: 8px;
		justify-content: flex-end;
	}
</style>
